/*
 * Copyright (c) 2026. Orion Analytics. All Rights Reserved
 */


import {useEffect, useState} from "react";
import Plot from "react-plotly.js";

export default function IndexPerformanceChart ({
    indices,
    allowedTickers = null,
    defaultTickers = null,
    title = "Compare Assets"
}) {
   // console.log('GLOBAL MARKET INDICES, --->',indices)

    const filteredIndices = allowedTickers
    ? indices.filter(row =>
        allowedTickers.includes(row.symbol)
      )
    : indices;

    const DEFAULT_TICKERS =
        defaultTickers ||
        (
            allowedTickers
                ? allowedTickers.slice(0, 2)
                : ["NIFTY 50", "S&P500"]
        );

    const TIMEFRAMES = {
        "1M": 30,
        "3M": 90,
        "6M": 180,
        "1Y": 365
    };

    const [timeframe, setTimeframe] =
        useState("1Y");

    const [chartMode, setChartMode] =
        useState("performance");

    const [selectedTickers, setSelectedTickers] =
        useState(DEFAULT_TICKERS);

    const filterDataByTimeframe = (data) => {

        const today = new Date();

        const cutoffDate = new Date();

        cutoffDate.setDate(
            today.getDate() -
            TIMEFRAMES[timeframe]
        );

        return data.filter(
            point =>
                new Date(point.date) >= cutoffDate
        );

    };

    const toggleTickerSelection = (ticker) => {

        setSelectedTickers(prev => {

            if (prev.includes(ticker)) {

                return prev.filter(
                    t => t !== ticker
                );

            }

            return [...prev, ticker];

        });

    };

    useEffect(() => {

        setSelectedTickers(
            defaultTickers || []
        );

    }, [defaultTickers, allowedTickers]);

    const transformDataForPlotly = (data) => {

        const grouped = {};

        data.forEach(row => {

            if (!grouped[row.symbol]) {
                grouped[row.symbol] = [];
            }

            grouped[row.symbol].push(row);

        });

        return Object.keys(grouped)

            .filter(ticker =>
                selectedTickers.includes(
                    ticker
                )
            )

            .map(ticker => {

                const rows =
                    grouped[ticker];

                const firstClose =
                    rows[0]?.close || 1;

                return {

                    x: rows.map(
                        r => r.date
                    ),

                    y: rows.map(r => {

                        if (
                            chartMode === "price" &&
                            selectedTickers.length === 1
                        ) {
                            return r.close;
                        }

                        return (
                            (
                                r.close /
                                firstClose
                            ) - 1
                        ) * 100;

                    }),

                    type: "scatter",

                    mode: "lines",

                    name: ticker,

                    line: {
                        width: 3
                    }

                };

            });

    };

    const filteredData =
    filterDataByTimeframe(filteredIndices);

    const plotData =
        transformDataForPlotly(
            filteredData
        );

    const uniqueTickers =
        Array.from(
            new Set(
                filteredIndices.map(i => i.symbol)
            )
        );

    return (

        <div className="space-y-6">

            {/* HEADER BAR */}

            <div className="
                flex
                flex-wrap
                justify-between
                gap-4
            ">

                {/* TIMEFRAMES */}

                    <div className="
                        flex
                        flex-row
                        gap-2
                        items-center
                    ">

                    {Object.keys(
                        TIMEFRAMES
                    ).map(key => (

                        <button
                            key={key}
                            onClick={() =>
                                setTimeframe(
                                    key
                                )
                            }
                            className={`
                                px-4
                                py-2
                                rounded-xl
                                text-sm
                                transition

                                ${
                                    timeframe === key
                                        ? "bg-emerald-500 text-white"
                                        : "bg-slate-900 text-slate-300"
                                }
                            `}
                        >
                            {key}
                        </button>

                    ))}

                </div>

                {/* CHART MODE */}

                <div className="
                    flex
                    gap-2
                ">

                    <button
                        onClick={() =>
                            setChartMode(
                                "performance"
                            )
                        }
                        className={`
                                h-10
                                px-4
                                rounded-lg
                                text-sm
                                whitespace-nowrap

                            ${
                                chartMode ===
                                "performance"
                                    ? "bg-blue-600 text-white"
                                    : "bg-slate-900 text-slate-300"
                            }
                        `}
                    >
                        Performance %
                    </button>

                    <button
                        onClick={() =>
                            setChartMode(
                                "price"
                            )
                        }
                        className={`
                                h-10
                                px-4
                                rounded-lg
                                text-sm
                                whitespace-nowrap
                            ${
                                chartMode ===
                                "price"
                                    ? "bg-blue-600 text-white"
                                    : "bg-slate-900 text-slate-300"
                            }
                        `}
                    >
                        Price
                    </button>

                </div>

            </div>

            {/* CHART + SIDEBAR */}

            <div className="
                grid
                grid-cols-12
                gap-6
            ">

                {/* MAIN CHART */}

                <div className="
                    col-span-12
                    lg:col-span-10
                ">

                    <div className="
                        bg-slate-950
                        rounded-3xl
                        border
                        border-slate-800
                        p-4
                    ">

                        <Plot
                            data={plotData}
                            layout={{
                                height: 550,

                                paper_bgcolor:
                                    "#020617",

                                plot_bgcolor:
                                    "#020617",

                                font: {
                                    color:
                                        "#cbd5e1"
                                },

                                margin: {
                                    l: 50,
                                    r: 20,
                                    t: 20,
                                    b: 40
                                },

                                xaxis: {
                                    type: "date",
                                    showgrid: false,
                                    rangeslider: {
                                        visible: false
                                    }
                                },

                                yaxis: {

                                    title:
                                        selectedTickers.length > 1 ||
                                        chartMode === "performance"
                                            ? "% Return"
                                            : "Index Value",

                                    showgrid: true,

                                    gridcolor:
                                        "#1e293b"
                                },

                                legend: {
                                    orientation:
                                        "h"
                                }
                            }}
                            config={{
                                responsive: true,
                                displayModeBar: false
                            }}
                            style={{
                                width: "100%"
                            }}
                        />

                    </div>

                </div>

                {/* INDEX SELECTOR */}

                <div className="
                    col-span-12
                    lg:col-span-2
                ">

                    <div className="
                        bg-slate-950
                        rounded-3xl
                        border
                        border-slate-800
                        p-4
                    ">

                        <h3 className="
                            text-white
                            font-semibold
                            mb-4
                        ">
                            {title}
                        </h3>

                        <div className="
                            space-y-2
                            max-h-[600px]
                            overflow-y-auto
                        ">

                            {uniqueTickers.map(
                                ticker => (

                                    <button
                                        key={ticker}
                                        onClick={() =>
                                            toggleTickerSelection(
                                                ticker
                                            )
                                        }
                                        className={`
                                            w-full
                                            text-left
                                            px-3
                                            py-2
                                            rounded-xl
                                            transition

                                            ${
                                                selectedTickers.includes(
                                                    ticker
                                                )
                                                    ? "bg-emerald-500 text-white"
                                                    : "bg-slate-900 text-slate-300 hover:bg-slate-800"
                                            }
                                        `}
                                    >
                                        {ticker}
                                    </button>

                                )
                            )}

                        </div>

                    </div>

                </div>

            </div>

        </div>

    );

};